/**
 * @fileoverview Connection dialog for the Service Bus Explorer.
 * @description Create or edit a saved connection: name it, authenticate with a
 *              SAS connection string or Microsoft Entra ID (namespace only, no
 *              secret), choose Queue or Topic, and — when the credentials allow
 *              — auto-load queue/topic names and subscriptions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, TextField,
  Button, Typography, ToggleButton, ToggleButtonGroup, Autocomplete, IconButton,
  InputAdornment, Alert, CircularProgress, Tooltip, Divider,
} from '@mui/material';
import {
  Key, Eye, EyeOff, Inbox, Layers, RefreshCw, Database, X, CheckCircle, ShieldCheck,
} from 'lucide-react';
import { serviceBusExplorerApi } from '../services/serviceBusExplorerApi';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#fff',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,151,169,0.10)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0097A9' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0097A9', borderWidth: 2,
  },
};

/* Canonical marker for a keyless, Entra ID authenticated target. */
const ENTRA_MARKER = 'Authentication=Managed Identity';

const emptyForm = {
  name: '', authMode: 'sas', connectionString: '', namespace: '',
  kind: 'queue', entityName: '', subscriptionName: '',
};

/* Extract the EntityPath (queue/topic) baked into a connection string. */
function entityPathOf(connStr) {
  const m = /EntityPath=([^;]+)/i.exec(connStr || '');
  return m ? m[1].trim() : '';
}

/* A target without a SharedAccessKey is authenticated with Entra ID. */
function isEntraTarget(connStr) {
  return Boolean(String(connStr || '').trim()) && !/SharedAccessKey\s*=/i.test(connStr);
}

/* Trim trailing '/' with a linear scan. A /\/+$/ regex backtracks super-linearly
   on a long run of slashes that is not at the end of the string (S5852). */
function trimTrailingSlashes(value) {
  const s = String(value || '');
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === 47 /* '/' */) end--;
  return s.slice(0, end);
}

/* Read the namespace host out of either target form. */
function namespaceOf(connStr) {
  const m = /Endpoint\s*=\s*(?:sb|amqps|https?):\/\/([^/;]+)/i.exec(connStr || '');
  if (m) return trimTrailingSlashes(m[1]);
  const first = trimTrailingSlashes(
    String(connStr || '').split(';')[0].trim().replace(/^sb:\/\//i, '')
  );
  return /\.servicebus\./i.test(first) ? first : '';
}

/* Compose the canonical Entra target for a namespace host. */
function buildEntraTarget(namespace) {
  const host = trimTrailingSlashes(
    String(namespace || '').trim().replace(/^sb:\/\//i, '')
  );
  return host ? `Endpoint=sb://${host}/;${ENTRA_MARKER}` : '';
}

export default function ConnectionDialog({ open, onClose, onSaved, initial }) {
  const [form, setForm] = useState(emptyForm);
  const [showConnStr, setShowConnStr] = useState(false);
  const [queues, setQueues] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = Boolean(initial?.id);
  const isEntra = form.authMode === 'entra';

  /* The value actually sent to the API, whichever auth mode is selected. */
  const target = isEntra
    ? buildEntraTarget(form.namespace)
    : form.connectionString.trim();

  /* When the connection string carries an EntityPath, the queue/topic is fixed. */
  const entityPath = entityPathOf(target);

  /* Initialize the form whenever the dialog opens. */
  useEffect(() => {
    if (!open) return;
    const conn = initial?.connectionString || '';
    const entra = isEntraTarget(conn);
    setForm(initial ? {
      name: initial.name || '',
      authMode: entra ? 'entra' : 'sas',
      connectionString: entra ? '' : conn,
      namespace: entra ? namespaceOf(conn) : '',
      kind: initial.kind || 'queue',
      entityName: initial.entityName || '',
      subscriptionName: initial.subscriptionName || '',
    } : emptyForm);
    setQueues([]); setTopics([]); setSubscriptions([]);
    setError(''); setShowConnStr(false);
  }, [open, initial]);

  /* When editing an existing connection, auto-load its names on open. */
  useEffect(() => {
    if (!(open && initial?.connectionString)) return;
    const conn = initial.connectionString;
    const ep = entityPathOf(conn);
    const kind = initial.kind || 'queue';
    if (ep) {
      // Entity is fixed by EntityPath — only subscriptions are listable.
      if (kind === 'topic') {
        loadSubscriptions(initial.entityName || ep, { conn, silent: true });
      }
    } else {
      loadEntities({ conn, silent: true });
      if (kind === 'topic' && initial.entityName) {
        loadSubscriptions(initial.entityName, { conn, silent: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const loadEntities = useCallback(async ({ conn, silent = false } = {}) => {
    const c = String(conn ?? target).trim();
    if (!c) { if (!silent) setError('Enter a connection string or namespace first.'); return; }
    if (!silent) setError('');
    setLoadingEntities(true);
    try {
      const res = await serviceBusExplorerApi.listEntities(c);
      if (res.error) {
        if (!silent) setError(`Couldn't load names — the credentials likely lack namespace-level management rights. You can still type names manually.`);
        setQueues([]); setTopics([]);
      } else {
        setQueues(res.queues || []);
        setTopics(res.topics || []);
      }
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load names.');
    } finally {
      setLoadingEntities(false);
    }
  }, [target]);

  const loadSubscriptions = useCallback(async (topic, { silent = false, conn } = {}) => {
    const c = String(conn ?? target).trim();
    const t = String(topic || '').trim();
    if (!c || !t) { setSubscriptions([]); return; }
    setLoadingSubs(true);
    try {
      const res = await serviceBusExplorerApi.listSubscriptions(c, t);
      if (res.error) {
        setSubscriptions([]);
        if (!silent) setError(`Couldn't load subscriptions — the credentials may lack management rights.`);
      } else {
        setSubscriptions(res.subscriptions || []);
      }
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load subscriptions.');
    } finally {
      setLoadingSubs(false);
    }
  }, [target]);

  const handleSave = async () => {
    const name = form.name.trim();
    const conn = target;
    const effectiveEntity = (form.entityName.trim() || entityPath).trim();
    if (!name) { setError('Please give this connection a name.'); return; }
    if (!conn) {
      setError(isEntra
        ? 'Namespace is required (e.g. my-namespace.servicebus.windows.net).'
        : 'Connection string is required.');
      return;
    }
    if (form.kind === 'topic' && !effectiveEntity) {
      setError('Enter the topic name (or include EntityPath in the connection string).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const meta = {
        kind: form.kind,
        entityName: effectiveEntity,
        subscriptionName: form.kind === 'topic' ? form.subscriptionName.trim() : '',
      };
      const res = await serviceBusExplorerApi.saveConnection(name, conn, meta);
      onSaved({
        id: res.id,
        name,
        kind: form.kind,
        entityName: meta.entityName,
        subscriptionName: meta.subscriptionName,
        connectionString: conn,
      });
    } catch (err) {
      setError(err.message || 'Failed to save connection.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Database size={20} color="#0097A9" />
            <Typography variant="h6" fontWeight={700}>
              {isEdit ? 'Edit connection' : 'New connection'}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: '#eef2f7' }}>
        <Stack spacing={2.5} mt={0.5}>
          <TextField
            label="Connection name" placeholder="e.g. Crew Validity (Test)"
            value={form.name} onChange={(e) => setField('name', e.target.value)}
            size="small" fullWidth autoFocus sx={inputSx}
          />

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.75}>
              How should we authenticate?
            </Typography>
            <ToggleButtonGroup
              value={form.authMode} exclusive size="small"
              onChange={(_, val) => {
                if (!val || val === form.authMode) return;
                setForm(prev => ({ ...prev, authMode: val }));
                setQueues([]); setTopics([]); setSubscriptions([]);
                setError('');
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 0.75,
                  '&.Mui-selected': { bgcolor: '#0097A9', color: '#fff', '&:hover': { bgcolor: '#007f8f' } },
                },
              }}
            >
              <ToggleButton value="sas"><Key size={14} style={{ marginRight: 6 }} /> Connection string</ToggleButton>
              <ToggleButton value="entra"><ShieldCheck size={14} style={{ marginRight: 6 }} /> Microsoft Entra ID</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {isEntra ? (
            <TextField
              label="Service Bus namespace"
              placeholder="my-namespace.servicebus.windows.net"
              value={form.namespace}
              onChange={(e) => setField('namespace', e.target.value)}
              onBlur={() => { if (form.namespace.trim()) loadEntities({ silent: true }); }}
              size="small" fullWidth sx={inputSx}
              helperText="No secret stored. The portal signs in with its own Entra identity, which needs the 'Azure Service Bus Data Receiver' role (or 'Data Owner' to list entities) on this namespace."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><ShieldCheck size={16} color="#64748b" /></InputAdornment>
                ),
              }}
            />
          ) : (
            <TextField
              label="Service Bus connection string"
              placeholder="Endpoint=sb://...;SharedAccessKeyName=...;SharedAccessKey=...;EntityPath=..."
              value={form.connectionString}
              onChange={(e) => setField('connectionString', e.target.value)}
              onBlur={() => {
                const conn = form.connectionString.trim();
                if (!conn) return;
                const ep = entityPathOf(conn);
                if (ep) { if (form.kind === 'topic') loadSubscriptions(ep, { silent: true }); }
                else loadEntities({ silent: true });
              }}
              size="small" fullWidth type={showConnStr ? 'text' : 'password'} sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Key size={16} color="#64748b" /></InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConnStr(s => !s)}>
                      {showConnStr ? <EyeOff size={16} /> : <Eye size={16} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.75}>
              What are you connecting to?
            </Typography>
            <ToggleButtonGroup
              value={form.kind} exclusive size="small"
              onChange={(_, val) => {
                if (!val) return;
                setField('kind', val);
                setError('');
                if (!target) return;
                if (entityPath) {
                  // Entity is fixed by EntityPath — only subscriptions are listable.
                  if (val === 'topic') loadSubscriptions(entityPath, { silent: true });
                } else {
                  loadEntities({ silent: true });
                  if (val === 'topic' && form.entityName.trim()) {
                    loadSubscriptions(form.entityName, { silent: true });
                  }
                }
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 0.75,
                  '&.Mui-selected': { bgcolor: '#0097A9', color: '#fff', '&:hover': { bgcolor: '#007f8f' } },
                },
              }}
            >
              <ToggleButton value="queue"><Inbox size={14} style={{ marginRight: 6 }} /> Queue</ToggleButton>
              <ToggleButton value="topic"><Layers size={14} style={{ marginRight: 6 }} /> Topic</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider sx={{ borderColor: '#eef2f7' }} />

          {entityPath ? (
            /* Queue/topic is fixed by the connection string's EntityPath. */
            <Stack spacing={2}>
              <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #eef2f7' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {form.kind === 'topic' ? 'Topic' : 'Queue'} (from connection string)
                </Typography>
                <Typography sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem', color: '#0f172a' }}>
                  {entityPath}
                </Typography>
              </Box>

              {form.kind === 'topic' && (
                <Autocomplete
                  freeSolo fullWidth options={subscriptions}
                  inputValue={form.subscriptionName}
                  onInputChange={(_, val) => setField('subscriptionName', val)}
                  sx={inputSx}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Subscription name"
                      placeholder="e.g. FPS-QA" sx={inputSx}
                      helperText={subscriptions.length
                        ? `${subscriptions.length} subscription${subscriptions.length > 1 ? 's' : ''} found`
                        : 'Type it, or use ↻ to load (listing needs Manage rights)'}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start"><Layers size={15} color="#64748b" /></InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            <Tooltip title="Load subscriptions">
                              <span>
                                <IconButton size="small" disabled={loadingSubs}
                                  onClick={() => loadSubscriptions(entityPath)}>
                                  {loadingSubs ? <CircularProgress size={13} /> : <RefreshCw size={13} color="#0097A9" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Stack>
          ) : (
            /* No EntityPath — choose from the namespace (needs Manage rights). */
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {form.kind === 'topic'
                    ? 'Pick a topic and subscription, or type them.'
                    : 'Pick a queue, or type it.'}
                </Typography>
                <Button
                  size="small" variant="outlined"
                  startIcon={loadingEntities ? <CircularProgress size={13} color="inherit" /> : <RefreshCw size={13} />}
                  onClick={() => loadEntities()} disabled={loadingEntities || !target}
                  sx={{
                    borderColor: '#0097A9', color: '#0097A9', borderRadius: '8px',
                    textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                    '&:hover': { borderColor: '#007f8f', bgcolor: 'rgba(0,151,169,0.04)' },
                  }}
                >
                  {loadingEntities ? 'Loading…' : 'Load from namespace'}
                </Button>
              </Stack>

              {form.kind === 'queue' ? (
                <Autocomplete
                  freeSolo fullWidth options={queues}
                  inputValue={form.entityName}
                  onInputChange={(_, val) => setField('entityName', val)}
                  sx={inputSx}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Queue name"
                      placeholder="e.g. flightcrewchange-inbound" sx={inputSx} />
                  )}
                />
              ) : (
                <>
                  <Autocomplete
                    freeSolo fullWidth options={topics}
                    inputValue={form.entityName}
                    onInputChange={(_, val) => setField('entityName', val)}
                    onChange={(_, val) => { if (val) loadSubscriptions(val, { silent: true }); }}
                    onBlur={() => { if (form.entityName.trim()) loadSubscriptions(form.entityName, { silent: true }); }}
                    sx={inputSx}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Topic name"
                        placeholder="e.g. as.sb.crew.validity" sx={inputSx} />
                    )}
                  />
                  <Autocomplete
                    freeSolo fullWidth options={subscriptions}
                    inputValue={form.subscriptionName}
                    onInputChange={(_, val) => setField('subscriptionName', val)}
                    sx={inputSx}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Subscription name"
                        placeholder="e.g. FPS-QA" sx={inputSx}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start"><Layers size={15} color="#64748b" /></InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              <Tooltip title="Load subscriptions">
                                <span>
                                  <IconButton size="small" disabled={loadingSubs}
                                    onClick={() => loadSubscriptions(form.entityName)}>
                                    {loadingSubs ? <CircularProgress size={13} /> : <RefreshCw size={13} color="#0097A9" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </>
              )}
            </>
          )}

          {error && <Alert severity="warning" sx={{ borderRadius: '10px' }}>{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <CheckCircle size={15} />}
          sx={{ bgcolor: '#0097A9', textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3, '&:hover': { bgcolor: '#007f8f' } }}
        >
          {isEdit ? 'Save changes' : 'Add connection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
